import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import Modal from "../modal";
import {
  Box,
  Divider,
  Heading,
  HStack,
  Pressable,
  Text,
  VStack,
} from "native-base";
import { Link, useLocation } from "react-router-dom";
import styles from "./styles.module.css";
import colors from "../../constants/colors";
import { formatToCurrency } from "../../utils";
import { FiChevronLeft, FiLock } from "react-icons/fi";
import { AiOutlineCreditCard } from "react-icons/ai";
import { MdOutlineAccountBalanceWallet, MdRefresh } from "react-icons/md";
import { BiTransfer } from "react-icons/bi";
import { StateContext, StatusContext } from "../../contexts";
import API from "../../api";
import routes from "../../routes";

const TYPES = {
  CARD: "card",
  BALANCE: "balance",
  TRANSFER: "transfer",
  CREDIT: "credit",
};

function PaymentModal(props) {
  const {
    isOpen,
    onRequestClose,
    label,
    editable,
    hideBalance,
    hideTransfer,
    defaultType,
    maxAmount,
    title,
    role,
    isRecharge,
  } = props;
  const state = useContext(StateContext);
  const status = useContext(StatusContext);
  const location = useLocation();
  const [amount, setAmount] = useState("");
  const [type, setType] = useState(defaultType);
  const [isLoading, setIsLoading] = useState(false);
  const [balance, setBalance] = useState(0);
  const [file, setFile] = useState("");
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  useEffect(() => {
    setAmount(String(props.amount));
  }, [props.amount]);

  const getBalance = useCallback(async () => {
    try {
      if (!state.jwt) {
        return;
      }
      setIsLoadingBalance(true);
      const {
        data: {
          data: { balance: _balance },
        },
      } = await API(state.jwt).get(`/${role}-movements/balance`);
      setBalance(_balance);
    } catch (error) {
      status.updateErrorMessage(error);
    } finally {
      setIsLoadingBalance(false);
    }
  }, [role, state.jwt, status]);

  useEffect(() => {
    if (!hideBalance) {
      getBalance();
    }
  }, [getBalance, hideBalance]);

  useEffect(() => {
    setType(defaultType);
  }, [defaultType]);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(false);
      if (type === TYPES.BALANCE) {
        getBalance();
      }
    } else {
      setAmount(props?.amount || "");
    }
  }, [getBalance, isOpen, props?.amount, type]);

  const onPay = () => {
    setIsLoading(true);
    switch (type) {
      case TYPES.CARD:
      case TYPES.BALANCE:
      case TYPES.TRANSFER:
        props.onPay(type, amount, file, () => setIsLoading(false));
        break;
      default:
        setIsLoading(false);
        break;
    }
  };

  const insufficientBalance = useMemo(
    () => type === TYPES.BALANCE && balance < amount,
    [type, balance, amount],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      maxWidth={700}
      isOpen={isOpen}
      onRequestClose={isLoading ? null : onRequestClose}>
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          onPay();
        }}>
        <VStack padding={5} space={3}>
          <Heading color={colors.secondary} fontSize="2xl">
            {title}
          </Heading>
          <VStack space={1}>
            <HStack>
              <Text bold color={colors.secondary}>
                {label}
              </Text>
              {!editable && <Text bold> {formatToCurrency(amount)}</Text>}
            </HStack>
            {editable && (
              <input
                type="number"
                step={1}
                min={20}
                className={styles.input}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                autoFocus
                disabled={isLoading}
                max={typeof maxAmount === "number" ? maxAmount : undefined}
              />
            )}
            {type === TYPES.TRANSFER && (
              <VStack marginTop={2}>
                <Text bold color={colors.secondary}>
                  Seleccione un archivo
                </Text>
                <input
                  type="file"
                  className={styles.input}
                  onChange={(e) => setFile(e.target.files[0])}
                  required
                  disabled={isLoading}
                />
              </VStack>
            )}
          </VStack>
          <div />
          <HStack space={5}>
            <Heading color={colors.secondary} fontSize="2xl">
              Tramitar pago
            </Heading>
            <HStack space={2}>
              <FiLock color={colors.gray} />
              <Text color={colors.gray}>Seguro</Text>
            </HStack>
          </HStack>
          <Divider backgroundColor={colors.primary} />
          <div />
          <HStack space={5}>
            <VStack flex={1} space={5}>
              <Heading color={colors.secondary} fontSize="xl">
                Elige un método de pago
              </Heading>
              <HStack space={3} alignItems="stretch">
                {/* TODO: Optimize, use components */}
                <Pressable
                  onPress={() => setType(TYPES.CARD)}
                  disabled={isLoading}>
                  <VStack
                    paddingY={4}
                    paddingX={5}
                    borderColor={
                      type === TYPES.CARD ? colors.primary : colors.black
                    }
                    borderWidth={2}
                    borderRadius={5}
                    alignItems="center"
                    height="full">
                    <AiOutlineCreditCard
                      color={
                        type === TYPES.CARD ? colors.primary : colors.black
                      }
                      size={40}
                    />
                    <Text bold fontSize="sm">
                      Tarjeta de
                    </Text>
                    <Text bold fontSize="sm">
                      crédito/débito
                    </Text>
                  </VStack>
                </Pressable>
                {!hideTransfer && (
                  <Pressable
                    onPress={() => setType(TYPES.TRANSFER)}
                    disabled={isLoading}>
                    <VStack
                      paddingY={4}
                      paddingX={5}
                      borderColor={
                        type === TYPES.TRANSFER ? colors.primary : colors.black
                      }
                      borderWidth={2}
                      borderRadius={5}
                      alignItems="center"
                      height="full">
                      <BiTransfer
                        color={
                          type === TYPES.TRANSFER
                            ? colors.primary
                            : colors.black
                        }
                        size={40}
                      />
                      <Text bold fontSize="sm">
                        Transferencia
                      </Text>
                      <Text bold fontSize="sm">
                        bancaria
                      </Text>
                    </VStack>
                  </Pressable>
                )}
                {!hideBalance && (
                  <Pressable
                    onPress={() => setType(TYPES.BALANCE)}
                    disabled={isLoading}>
                    <VStack
                      paddingY={4}
                      paddingX={5}
                      borderColor={
                        type === TYPES.BALANCE ? colors.primary : colors.black
                      }
                      borderWidth={2}
                      borderRadius={5}
                      alignItems="center"
                      height="full">
                      <MdOutlineAccountBalanceWallet
                        color={
                          type === TYPES.BALANCE ? colors.primary : colors.black
                        }
                        size={40}
                      />
                      <Text bold fontSize="sm">
                        Usar mi saldo
                      </Text>
                    </VStack>
                  </Pressable>
                )}
              </HStack>
            </VStack>
            {isRecharge && type === TYPES.TRANSFER && (
              <VStack>
                <Text bold>Nombre del beneficiario:</Text>
                <Text>Ticket Bus Plus S.A. de C.V.</Text>
                <Text bold>Número de cuenta:</Text>
                <Text>Clave 65501766064</Text>
                <Text>Interbancaria 014180655017660642</Text>
                <Text bold>Nombre del banco:</Text>
                <Text>Santander</Text>
              </VStack>
            )}
          </HStack>
          <HStack minH={7}>
            {type === TYPES.BALANCE && (
              <VStack space={4}>
                <Text>Tu saldo actual es de {formatToCurrency(balance)}</Text>
                {insufficientBalance && (
                  <HStack space={3}>
                    <Text>Saldo insuficiente</Text>
                    <button
                      disabled={isLoadingBalance}
                      onClick={getBalance}
                      type="button">
                      <Box
                        backgroundColor={colors.primary}
                        borderRadius="md"
                        paddingX={3}
                        paddingY={1}>
                        <MdRefresh color={colors.white} size={24} />
                      </Box>
                    </button>
                    <Link
                      to={{
                        pathname:
                          role === "agency"
                            ? routes.agencyProfileBalance
                            : routes.profileBalance,
                        search: `modal=true&url=${location.pathname}`,
                      }}
                      state={location.state}>
                      <Box
                        backgroundColor={colors.primary}
                        borderRadius="md"
                        paddingX={3}
                        paddingY={1}>
                        <Text color={colors.white}>Recarga ahora</Text>
                      </Box>
                    </Link>
                  </HStack>
                )}
              </VStack>
            )}
          </HStack>
          <HStack space={5}>
            <Pressable onPress={onRequestClose} disabled={isLoading}>
              <HStack
                backgroundColor={colors.lightGray}
                paddingY={3}
                paddingX={5}
                borderRadius="full">
                <FiChevronLeft color={colors.white} size={20} />
                <Text color={colors.white}>Regresar</Text>
              </HStack>
            </Pressable>
            <button disabled={isLoading || insufficientBalance}>
              <Box
                backgroundColor={colors.primary}
                paddingY={3}
                paddingX={50}
                borderRadius="full">
                <Text color={colors.white}>Continuar</Text>
              </Box>
            </button>
          </HStack>
        </VStack>
      </form>
    </Modal>
  );
}

PaymentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  onPay: PropTypes.func.isRequired,
  editable: PropTypes.bool,
  hideBalance: PropTypes.bool,
  hideCredit: PropTypes.bool,
  hideTransfer: PropTypes.bool,
  defaultType: PropTypes.string,
  maxAmount: PropTypes.number,
  title: PropTypes.string,
  role: PropTypes.string,
  isRecharge: PropTypes.bool,
};

PaymentModal.defaultProps = {
  editable: false,
  hideBalance: false,
  hideCredit: false,
  hideTransfer: true,
  defaultType: TYPES.CARD,
  title: "Finalizar compra",
  role: "agency",
  isRecharge: false,
};

export default React.memo(PaymentModal);
